/**
 * Icon set, ported from the frozen prototype's `ICON` map plus the six icons it inlines in the
 * sidebar markup. Every path is copied verbatim; sizing comes from the CSS the icon sits in
 * (`.btn svg` 16px, `.icon-btn svg` 15px, `.nav a svg` 17px, `.dropzone svg` 34px).
 *
 * Two prototype defects are fixed rather than reproduced, both recorded in the visual-parity
 * notes: `file` carried the `check` path (so it drew a tick), and `link` was declared but unused.
 */
import type { ReactElement, SVGProps } from 'react';

export type IconName =
  | 'eye'
  | 'send'
  | 'x'
  | 'copy'
  | 'ext'
  | 'upload'
  | 'check'
  | 'file'
  | 'edit'
  | 'alignLeft'
  | 'alignCenter'
  | 'alignRight'
  | 'print'
  | 'link'
  | 'dashboard'
  | 'assets'
  | 'templates'
  | 'import'
  | 'credentials'
  | 'settings'
  | 'people'
  | 'audit';

interface Shape {
  strokeWidth: number;
  paths: string[];
  circles?: { cx: number; cy: number; r: number }[];
  rects?: { x: number; y: number; width: number; height: number; rx?: number }[];
}

const SHAPES: Record<IconName, Shape> = {
  eye: {
    strokeWidth: 1.8,
    paths: ['M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z'],
    circles: [{ cx: 12, cy: 12, r: 3 }],
  },
  send: { strokeWidth: 1.8, paths: ['M22 2L11 13', 'M22 2l-7 20-4-9-9-4 20-7z'] },
  x: { strokeWidth: 2, paths: ['M18 6L6 18M6 6l12 12'] },
  copy: {
    strokeWidth: 1.8,
    paths: ['M5 15V5a2 2 0 0 1 2-2h10'],
    rects: [{ x: 9, y: 9, width: 12, height: 12, rx: 2 }],
  },
  ext: {
    strokeWidth: 1.8,
    paths: ['M14 4h6v6', 'M20 4l-9 9', 'M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5'],
  },
  upload: {
    strokeWidth: 1.6,
    paths: ['M12 16V5', 'M7 10l5-5 5 5', 'M4 17v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2'],
  },
  check: { strokeWidth: 2, paths: ['M20 6L9 17l-5-5'] },
  // the prototype's `file` duplicated the check path; this is the document it clearly meant
  file: {
    strokeWidth: 1.8,
    paths: ['M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z', 'M14 3v5h5'],
  },
  edit: { strokeWidth: 1.8, paths: ['M12 20h9', 'M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z'] },
  alignLeft: { strokeWidth: 2, paths: ['M4 6h16M4 12h10M4 18h14'] },
  alignCenter: { strokeWidth: 2, paths: ['M4 6h16M7 12h10M5 18h14'] },
  alignRight: { strokeWidth: 2, paths: ['M4 6h16M10 12h10M6 18h14'] },
  print: {
    strokeWidth: 1.8,
    paths: ['M6 9V3h12v6', 'M6 14h12v7H6z'],
    rects: [{ x: 3, y: 9, width: 18, height: 9, rx: 2 }],
  },
  link: {
    strokeWidth: 1.8,
    paths: ['M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1', 'M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1'],
  },
  dashboard: {
    strokeWidth: 1.8,
    paths: [],
    rects: [
      { x: 3, y: 3, width: 8, height: 8 },
      { x: 13, y: 3, width: 8, height: 8 },
      { x: 3, y: 13, width: 8, height: 8 },
      { x: 13, y: 13, width: 8, height: 8 },
    ],
  },
  assets: {
    strokeWidth: 1.8,
    paths: ['M4 17l5-5 4 4 3-2 4 4'],
    circles: [{ cx: 8.5, cy: 9.5, r: 1.8 }],
    rects: [{ x: 3, y: 4, width: 18, height: 16, rx: 2 }],
  },
  templates: { strokeWidth: 1.8, paths: ['M12 3l9 5-9 5-9-5 9-5z', 'M3 13l9 5 9-5'] },
  import: { strokeWidth: 1.8, paths: ['M12 16V4', 'M6 10l6-6 6 6', 'M4 20h16'] },
  credentials: {
    strokeWidth: 1.8,
    paths: ['M7 10h6M7 14h10'],
    rects: [{ x: 3, y: 5, width: 18, height: 14, rx: 2 }],
  },
  settings: {
    strokeWidth: 1.8,
    paths: ['M3 7l9 6 9-6'],
    rects: [{ x: 3, y: 5, width: 18, height: 14, rx: 2 }],
  },
  // recipients and audit have no prototype icon; drawn in the same 24×24 stroked language
  people: {
    strokeWidth: 1.8,
    paths: ['M16 20v-1a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v1', 'M17 4.5a3.5 3.5 0 0 1 0 7'],
    circles: [{ cx: 9.5, cy: 7.5, r: 3.5 }],
  },
  audit: {
    strokeWidth: 1.8,
    paths: ['M8 4h8a2 2 0 0 1 2 2v14l-3-2-3 2-3-2-3 2V6a2 2 0 0 1 2-2z', 'M9 9h6M9 13h4'],
  },
};

export function Icon({
  name,
  title,
  ...rest
}: { name: IconName; title?: string } & SVGProps<SVGSVGElement>): ReactElement {
  const shape = SHAPES[name];
  return (
    // An icon with no `title` is decorative: aria-hidden below removes it from the accessibility tree and the label lives on the control that contains it.
    // biome-ignore lint/a11y/noSvgWithoutTitle: decorative unless `title` is passed, in which case a <title> is rendered
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={shape.strokeWidth}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      focusable="false"
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      {shape.rects?.map((r) => (
        <rect key={`${r.x}-${r.y}-${r.width}`} {...r} />
      ))}
      {shape.circles?.map((c) => (
        <circle key={`${c.cx}-${c.cy}`} {...c} />
      ))}
      {shape.paths.map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}

/**
 * The brand mark: a red square with a smaller yellow square offset from its bottom-right corner.
 * The prototype repeats it at 26px (sidebar), 22px (LinkedIn preview) and 14px (e-mail), so it is
 * one component with a size.
 */
export function BrandMark({ size = 26 }: { size?: number }): ReactElement {
  const dot = Math.round(size * 0.385);
  const offset = -Math.round(size * 0.23);
  return (
    <span
      aria-hidden="true"
      style={{
        position: 'relative',
        display: 'inline-block',
        flex: 'none',
        width: size,
        height: size,
        background: 'var(--red)',
      }}
    >
      <span
        style={{
          position: 'absolute',
          right: offset,
          bottom: offset,
          width: dot,
          height: dot,
          background: 'var(--yellow)',
        }}
      />
    </span>
  );
}
